<script lang="ts" setup>
import PaginationTable from '@/components/tables/pagination-table/logic/PaginationTable.vue';
import type {
  PaginationQueryAction,
  PaginationQueryState,
} from '@/components/tables/pagination-table/types';
import { useElementSize } from '@vueuse/core';
import { computed, ref, watch } from 'vue';
import { useSelectableInvoiceTableColumns } from './selectable-invoice-table-columns';
import SelectableInvoiceTableRows from './selectable-invoice-table-rows.vue';
import type { SelectableInvoiceTableInvoice } from './types';

const props = defineProps<{
  rows: SelectableInvoiceTableInvoice[];
  totalRows: number;
  selectedIds: Set<string>;
  filters?: PaginationQueryState;
  isLoading?: boolean;
}>();

const emit = defineEmits<{
  (e: 'row-click', invoice: SelectableInvoiceTableInvoice): void;
  (e: 'navigate-to-invoice', invoice: SelectableInvoiceTableInvoice): void;
  (e: 'toggle-selection', id: string): void;
  (e: 'toggle-all'): void;
  (e: 'query-change', action: PaginationQueryAction): void;
}>();

const tableRef = ref<HTMLElement | null>(null);
const { width } = useElementSize(tableRef, { width: 9999, height: 0 });

// Breakpoints based on container width.
const HIDE_INVOICE_DATE_BELOW = 1500;
const HIDE_VAT_BELOW = 1380;
const HIDE_PAYMENT_FILE_BELOW = 1260;
const HIDE_INVOICE_NUMBER_BELOW = 1160;

const allColumns = [
  { key: '_actions', sortable: false, columnWidth: '10px' },
  ...useSelectableInvoiceTableColumns(),
];

const columns = computed(() =>
  allColumns.filter((col) => {
    if (col.key === 'invoiceDate' && width.value < HIDE_INVOICE_DATE_BELOW)
      return false;
    if (col.key === 'vat' && width.value < HIDE_VAT_BELOW) return false;
    if (col.key === 'paymentFileId' && width.value < HIDE_PAYMENT_FILE_BELOW)
      return false;
    if (col.key === 'invoiceNumber' && width.value < HIDE_INVOICE_NUMBER_BELOW)
      return false;
    return true;
  }),
);

const isAllSelected = computed(
  () =>
    props.rows.length > 0 &&
    props.rows.every((row) => props.selectedIds.has(row.id)),
);

const isSomeSelected = computed(
  () =>
    props.rows.some((row) => props.selectedIds.has(row.id)) &&
    !isAllSelected.value,
);

function onQueryChange(action: PaginationQueryAction) {
  emit('query-change', action);
}

const checkboxRef = ref<HTMLInputElement | null>(null);

watch(
  [isAllSelected, isSomeSelected],
  ([allSelected, someSelected]) => {
    if (checkboxRef.value) {
      checkboxRef.value.checked = allSelected;
      checkboxRef.value.indeterminate = someSelected;
    }
  },
  { flush: 'post' },
);
</script>

<template>
  <div class="invoice-table" ref="tableRef">
    <PaginationTable
      :columns="columns"
      :rows="props.rows"
      :total-items="props.totalRows"
      :query="filters"
      :is-loading="isLoading"
      @query-change="onQueryChange"
      @row-click="emit('row-click', $event)"
    >
      <template #header-_actions>
        <input
          ref="checkboxRef"
          type="checkbox"
          class="select-all-checkbox"
          @click.stop.prevent="emit('toggle-all')"
        />
      </template>
      <template #cell="{ row, column, value }">
        <SelectableInvoiceTableRows
          :row="row"
          :column="column"
          :value="value"
          :is-selected="selectedIds.has(row.id)"
          @toggle-selection="emit('toggle-selection', row.id)"
          @navigate-to-invoice="emit('navigate-to-invoice', row)"
        />
      </template>
    </PaginationTable>
  </div>
</template>

<style scoped lang="scss">
.invoice-table {
  :deep(table tbody) {
    tr {
      cursor: pointer;
    }

    tr:nth-child(even) td {
      background-color: #f2f3f6;
    }

    tr:hover td {
      background-color: #e2e4ea;
    }

    tr td {
      vertical-align: middle;
    }

    tr:has(.selection-checkbox:checked) td {
      background-color: #dbeafe;
    }

    tr:has(.selection-checkbox:checked):hover td {
      background-color: #bfdbfe;
    }

    tr.failed td {
      background-color: #fee2e2;
    }
  }

  :deep(table thead th:first-child) {
    text-align: center;
  }

  .select-all-checkbox {
    cursor: pointer;
    width: 15px;
    height: 15px;
    vertical-align: middle;
    display: block;
    margin-left: 0 auto;
  }
}
</style>
